import { BaseModel, type RelationConfig } from './base';

// Enums

export enum ContentTemplateType {
  serviceAutomation = 1,
  campaign = 2,
  customerMessage = 3,
}

export const ContentTemplateTypeLabels: Record<ContentTemplateType, string> = {
  [ContentTemplateType.serviceAutomation]: 'Service Automation',
  [ContentTemplateType.campaign]: 'Campaign',
  [ContentTemplateType.customerMessage]: 'Customer Message',
};

export enum ContentTemplateCustomerMessageType {
  newReservation = 1,
  newReservationConfirmed = 2,
  newReservationCancelled = 4,
  newReservationReminder = 5,
  reservationMoved = 6,
  reservationCancelled = 7,
  reservationReminder = 8,
  newSale = 9,
  passwordReset = 10,
  receivedCampaign = 11,
  general = 12,
  loginCode = 13,
  newCustomer = 14,
  newOrder = 15,
  newCustomerCourse = 16,
  waitlistOffer = 17,
  checkInReservation = 18,
  noShowReservation = 19,
  confirmReservation = 20,
  reviewReservation = 21,
  nextReservationReminder = 22,
  resetPasswordRequest = 23,
  massEmail = 24,
  massSms = 25,
  birthdayMessage = 26,
  passExpiringReminder = 27,
  passExpiredReminder = 28,
  formRequest = 29,
  orderPaymentRequest = 30,
  minisiteLogin = 31,
  waitlistAdd = 32,
  moveReservation = 33,
  form = 34,
  giftCard = 35,
  giftPass = 36,
  customerConfirmed = 38,
  serviceAutomationMessage = 39,
}

export const ContentTemplateCustomerMessageTypeLabels: Record<ContentTemplateCustomerMessageType, string> = {
  [ContentTemplateCustomerMessageType.newReservation]: 'New Appointment',
  [ContentTemplateCustomerMessageType.newReservationConfirmed]: 'New Appointment Confirmed',
  [ContentTemplateCustomerMessageType.newReservationCancelled]: 'New Appointment Cancelled',
  [ContentTemplateCustomerMessageType.newReservationReminder]: 'New Appointment Reminder',
  [ContentTemplateCustomerMessageType.reservationMoved]: 'Appointment Moved',
  [ContentTemplateCustomerMessageType.reservationCancelled]: 'Appointment Cancelled',
  [ContentTemplateCustomerMessageType.reservationReminder]: 'Appointment Reminder',
  [ContentTemplateCustomerMessageType.newSale]: 'New Sale',
  [ContentTemplateCustomerMessageType.passwordReset]: 'Password Reset',
  [ContentTemplateCustomerMessageType.receivedCampaign]: 'Received Campaign',
  [ContentTemplateCustomerMessageType.general]: 'General',
  [ContentTemplateCustomerMessageType.loginCode]: 'Login Code',
  [ContentTemplateCustomerMessageType.newCustomer]: 'New Customer',
  [ContentTemplateCustomerMessageType.newOrder]: 'New Order',
  [ContentTemplateCustomerMessageType.newCustomerCourse]: 'New Customer Course',
  [ContentTemplateCustomerMessageType.waitlistOffer]: 'Waitlist Offer',
  [ContentTemplateCustomerMessageType.checkInReservation]: 'Check In Appointment',
  [ContentTemplateCustomerMessageType.noShowReservation]: 'No Show Appointment',
  [ContentTemplateCustomerMessageType.confirmReservation]: 'Confirm Appointment',
  [ContentTemplateCustomerMessageType.reviewReservation]: 'Review Appointment',
  [ContentTemplateCustomerMessageType.nextReservationReminder]: 'Next Appointment Reminder',
  [ContentTemplateCustomerMessageType.resetPasswordRequest]: 'Reset Password Request',
  [ContentTemplateCustomerMessageType.massEmail]: 'Mass Email',
  [ContentTemplateCustomerMessageType.massSms]: 'Mass SMS',
  [ContentTemplateCustomerMessageType.birthdayMessage]: 'Birthday Message',
  [ContentTemplateCustomerMessageType.passExpiringReminder]: 'Pass Expiring Reminder',
  [ContentTemplateCustomerMessageType.passExpiredReminder]: 'Pass Expired Reminder',
  [ContentTemplateCustomerMessageType.formRequest]: 'Form Request',
  [ContentTemplateCustomerMessageType.orderPaymentRequest]: 'Order Payment Request',
  [ContentTemplateCustomerMessageType.minisiteLogin]: 'Minisite Login',
  [ContentTemplateCustomerMessageType.waitlistAdd]: 'Waitlist Add',
  [ContentTemplateCustomerMessageType.moveReservation]: 'Move Appointment',
  [ContentTemplateCustomerMessageType.form]: 'Form',
  [ContentTemplateCustomerMessageType.giftCard]: 'Gift Card',
  [ContentTemplateCustomerMessageType.giftPass]: 'Gift Pass',
  [ContentTemplateCustomerMessageType.customerConfirmed]: 'Customer Confirmed',
  [ContentTemplateCustomerMessageType.serviceAutomationMessage]: 'Service Automation Message',
};

// Helper Classes

export class ContentTemplateOverride extends BaseModel {
  slots!: { name: string; type: string; value: any }[];
  options!: { name: string; value: any }[];
  blocks!: { name: string; options: { name: string; value: any }[] }[];
}

export class ContentTemplateOption extends BaseModel {}

// Main Model

export class ContentTemplate extends BaseModel {
  idContentTemplate!: number;
  type!: ContentTemplateType;
  customerMessageType!: ContentTemplateCustomerMessageType;
  name!: string;
  emailSubject!: string;
  smsBody!: string;
  enabled!: boolean;
  overrides!: ContentTemplateOverride | null;
  options!: ContentTemplateOption | null;

  static override relations(): RelationConfig {
    return {
      overrides: { type: ContentTemplateOverride },
      options: { type: ContentTemplateOption },
    };
  }

  override objectID(): string | null {
    return this.idContentTemplate?.toString() ?? null;
  }

  override toJSON(): Record<string, any> {
    const object: Record<string, any> = {
      type: this.type,
      customerMessageType: this.customerMessageType,
      name: this.name,
      emailSubject: this.emailSubject,
      smsBody: this.smsBody,
      enabled: this.enabled,
    };

    if (this.overrides?.slots) {
      object.emailSlots = {};
      for (const slot of this.overrides.slots) {
        if (slot.value != null) {
          object.emailSlots[slot.name] = {
            type: slot.type,
            value: slot.value,
          };
        }
      }
    }

    object.emailOptions = {};

    if (this.overrides?.options) {
      for (const option of this.overrides.options) {
        object.emailOptions[option.name] = option.value;
      }
    }

    if (this.overrides?.blocks) {
      object.emailOptions.blocks = {};
      for (const block of this.overrides.blocks) {
        object.emailOptions.blocks[block.name] = {};
        for (const option of block.options) {
          object.emailOptions.blocks[block.name][option.name] = option.value;
        }
      }
    }

    return object;
  }
}
